import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";

import { useLang } from "@/i18n/LanguageContext";
import { getSpace } from "@/data/spaces";
import { CHAPTER_FIELDS, migrateChapter } from "@/data/projectChapters";

// -----------------------------------------------------------------------------
// Maison Dodji — Project Space renderer.
//
// Doctrine (frozen):
//   • Private per-project editorial environment
//   • Opened by the Maison AFTER a real conversation
//   • Each chapter is the written continuation of a conversation
//   • Access by opaque token URL
//   • noindex, nofollow on every Space (public and QA fixture alike)
//   • Sealed = immutable; visible seal banner + colophon
// -----------------------------------------------------------------------------

const FALLBACK_LANG = "fr";

// Set/remove noindex meta tag when the component mounts and unmounts,
// so search engines never index a Space route regardless of navigation path.
function useNoIndexMeta() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
}

// Set the document title from the Space chapter registry.
function useDocTitle(title) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => {
      document.title = prev;
    };
  }, [title]);
}

// Format a YYYY-MM-DD into an editorial date in the reader's language.
function editorialDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function ProjectSpace() {
  const { token } = useParams();
  const { lang, t } = useLang();

  // Static fixture (from bundled registry) OR dynamic (fetched from API)
  const staticSpace = useMemo(() => getSpace(token), [token]);
  const [dynamicSpace, setDynamicSpace] = useState(null);
  const [loading, setLoading] = useState(!staticSpace);

  useEffect(() => {
    if (staticSpace) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/space/${token}`);
        if (!cancelled && r.ok) setDynamicSpace(await r.json());
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token, staticSpace]);

  const space = staticSpace || dynamicSpace;

  useNoIndexMeta();
  useDocTitle(t.space.metaTitle);

  if (loading) return null;
  if (!space) {
    return <SpaceNotFound t={t} />;
  }

  // Dynamic (API) chapters: identify by absence of static fixture flag.
  // Legacy dynamic chapters (with plain `body` string) are migrated on the fly.
  const isDynamic = !space.fixture && Array.isArray(space.chapters);
  const dynamicChapters = isDynamic
    ? space.chapters.map(migrateChapter)
    : [];

  const pick = (bi) => (typeof bi === "string" ? bi : bi?.[lang] || bi?.[FALLBACK_LANG]);

  // Client sees only shared chapters (or everything if sealed).
  const visibleChapters = isDynamic
    ? dynamicChapters.filter((c) => c.shared || space.sealed)
    : space.chapters;

  return (
    <div
      className="min-h-screen bg-[hsl(var(--md-chalk))] text-[hsl(var(--md-ink))]"
      data-testid="project-space"
    >
      <div className="max-w-[820px] mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* Discreet return link to the Maison */}
        <div className="mb-16 md:mb-20">
          <Link
            to="/"
            className="font-eyebrow text-[hsl(var(--md-stone))] hover:text-[hsl(var(--md-ink))] transition-colors"
            data-testid="space-return-maison"
          >
            {t.space.returnMaison}
          </Link>
        </div>

        {/* Fixture banner — only for private QA fixtures */}
        {space.fixture && (
          <div
            data-testid="space-fixture-notice"
            className="mb-12 px-4 py-3 border border-[hsl(var(--md-line-strong))] bg-[hsl(var(--md-bone))] text-[12px] tracking-[0.14em] uppercase text-[hsl(var(--md-stone))] font-body"
          >
            {t.space.fixtureNotice}
          </div>
        )}

        {/* Sealed banner — only for sealed Spaces */}
        {space.sealed && (
          <div
            data-testid="space-sealed-banner"
            className="mb-12 px-4 py-3 border border-[hsl(var(--md-ink))] bg-[hsl(var(--md-ink))] text-[hsl(var(--md-chalk))] text-[12px] tracking-[0.22em] uppercase font-eyebrow"
          >
            {t.space.sealedBanner} — {t.space.sealedOn} {editorialDate(space.sealed.date, lang)}
          </div>
        )}

        {/* Named-Party eyebrow */}
        <p
          className="font-eyebrow text-[hsl(var(--md-stone))] mb-6"
          data-testid="space-eyebrow"
        >
          — {space.projectTitle} · {space.namedParty}
        </p>

        {/* Chapters */}
        <div className="space-y-24 md:space-y-28">
          {visibleChapters.map((chap, idx) => (
            isDynamic
              ? <DynamicChapter key={idx} chap={chap} t={t} />
              : <ChapterBlock key={idx} chap={chap} t={t} lang={lang} pick={pick} />
          ))}
        </div>

        {/* Colophon — always for sealed Spaces */}
        {space.sealed && <Colophon space={space} t={t} lang={lang} />}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Chapter blocks
// -----------------------------------------------------------------------------

function ChapterBlock({ chap, t, lang, pick }) {
  const title = t.space.chapters[chap.type === "closing" ? "closing" : chap.type];
  if (!title) return null;

  return (
    <section data-testid={`space-chapter-${chap.type}`}>
      <div className="chapter-hairline mb-8" />
      <h2 className="font-display text-[1.75rem] md:text-[2.25rem] leading-[1.15] text-[hsl(var(--md-ink))] mb-8">
        {title}
      </h2>

      {chap.type === "salutation" && (
        <ChapterProse paragraphs={pick(chap.body)} />
      )}

      {chap.type === "compris" && (
        <ChapterProse paragraphs={pick(chap.body)} />
      )}

      {chap.type === "references" && (
        <div className="space-y-12">
          {chap.items.map((it, i) => (
            <figure key={i} className="space-y-3">
              <div className="relative aspect-[4/3] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))] overflow-hidden">
                <img
                  src={it.image}
                  alt={it.alt}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <figcaption className="font-body text-[14.5px] leading-[1.7] text-[hsl(var(--md-stone))]">
                {pick(it.caption)}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {chap.type === "rythme" && (
        <ul className="space-y-4">
          {chap.entries.map((e, i) => (
            <li key={i} className="grid grid-cols-12 gap-4 items-baseline">
              <span className="col-span-12 md:col-span-4 font-eyebrow text-[hsl(var(--md-stone))]">
                {editorialDate(e.date, lang)}
              </span>
              <span className="col-span-12 md:col-span-8 font-body text-[15px] text-[hsl(var(--md-ink))]">
                {pick(e.phase)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {chap.type === "questions" && (
        <ol className="space-y-6 list-decimal list-inside">
          {chap.prompts.map((p, i) => (
            <li key={i} className="font-display text-[1.15rem] text-[hsl(var(--md-ink))]">
              {pick(p)}
            </li>
          ))}
        </ol>
      )}

      {chap.type === "correspondance" && (
        <ul className="space-y-6">
          {chap.entries.map((e, i) => (
            <li key={i}>
              <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-1">
                {editorialDate(e.date, lang)}
              </p>
              <p className="font-body text-[15px] leading-[1.75] text-[hsl(var(--md-ink))]">
                {pick(e.note)}
              </p>
            </li>
          ))}
        </ul>
      )}

      {chap.type === "proposition" && (
        <ChapterProse paragraphs={pick(chap.body)} />
      )}

      {chap.type === "closing" && (
        <div className="space-y-6">
          <ChapterProse paragraphs={pick(chap.body)} />
          {chap.finalImage && (
            <figure className="pt-6">
              <div className="relative aspect-[4/3] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))] overflow-hidden">
                <img
                  src={chap.finalImage.src}
                  alt={chap.finalImage.alt || ""}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              {chap.finalImage.caption && (
                <figcaption className="font-body text-[14px] text-[hsl(var(--md-stone))] mt-3">
                  {pick(chap.finalImage.caption)}
                </figcaption>
              )}
            </figure>
          )}
        </div>
      )}
    </section>
  );
}

function ChapterProse({ paragraphs }) {
  if (!paragraphs) return null;
  const list = Array.isArray(paragraphs) ? paragraphs : [paragraphs];
  return (
    <div className="space-y-5 font-body text-[16px] leading-[1.85] text-[hsl(var(--md-ink))]">
      {list.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

// Renders a dynamic (API-authored) structured chapter.
// Reads the type-specific schema from CHAPTER_FIELDS and displays each populated
// field with a discreet label. Empty fields are silently omitted.
function DynamicChapter({ chap, t }) {
  const title = t.space.chapters[chap.type] || t.space.chapters.salutation;
  const spec = CHAPTER_FIELDS[chap.type] || [];
  const labels = t.space.fields || {};
  const fields = chap.fields || {};

  return (
    <section data-testid={`space-chapter-${chap.type}`}>
      <div className="chapter-hairline mb-8" />
      <h2 className="font-display text-[1.75rem] md:text-[2.25rem] leading-[1.15] text-[hsl(var(--md-ink))] mb-8">
        {title}
      </h2>

      <div className="space-y-6">
        {spec.map((f) => {
          const v = fields[f.key];
          if (f.kind === "list") {
            const list = Array.isArray(v) ? v : [];
            if (list.length === 0) return null;
            return <DynamicList key={f.key} f={f} list={list} labels={labels} />;
          }
          if (!v) return null;
          if (f.kind === "select") {
            const opt = f.options.find((o) => o.v === v);
            const rendered = opt ? labels[opt.labelKey] : v;
            return <DynamicScalar key={f.key} label={labels[f.labelKey]} value={rendered} />;
          }
          if (f.kind === "textarea") {
            return <DynamicProse key={f.key} label={labels[f.labelKey]} text={v} />;
          }
          return <DynamicScalar key={f.key} label={labels[f.labelKey]} value={v} />;
        })}
      </div>

      {chap.observations && (
        <div className="mt-8 pt-6 border-t border-[hsl(var(--md-line))]">
          <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-2">
            {labels.observations || "Observations"}
          </p>
          <p className="font-body italic text-[15.5px] leading-[1.85] text-[hsl(var(--md-ink))] whitespace-pre-line">
            {chap.observations}
          </p>
        </div>
      )}
    </section>
  );
}

function DynamicScalar({ label, value }) {
  return (
    <div className="grid grid-cols-12 gap-4">
      <dt className="col-span-12 md:col-span-4 font-eyebrow text-[hsl(var(--md-stone))]">{label}</dt>
      <dd className="col-span-12 md:col-span-8 font-body text-[15px] text-[hsl(var(--md-ink))]">{value}</dd>
    </div>
  );
}

function DynamicProse({ label, text }) {
  const paragraphs = String(text).split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <div>
      <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-2">{label}</p>
      <div className="space-y-4 font-body text-[15.5px] leading-[1.85] text-[hsl(var(--md-ink))] whitespace-pre-line">
        {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  );
}

function DynamicList({ f, list, labels }) {
  const isImageList = f.itemFields.some((s) => s.key === "imageUrl");
  if (isImageList) {
    return (
      <div className="space-y-8">
        {list.map((item, i) => (
          item.imageUrl ? (
            <figure key={i} className="space-y-3">
              <div className="relative aspect-[4/3] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))] overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.caption || ""}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              {item.caption && (
                <figcaption className="font-body text-[14px] text-[hsl(var(--md-stone))]">
                  {item.caption}
                </figcaption>
              )}
            </figure>
          ) : null
        ))}
      </div>
    );
  }
  return (
    <ul className="space-y-4">
      {list.map((item, i) => (
        <li key={i} className="border-l border-[hsl(var(--md-line-strong))] pl-4 py-1">
          {f.itemFields.map((sub) => (
            item[sub.key] ? (
              <p key={sub.key} className="font-body text-[15px] leading-[1.7] text-[hsl(var(--md-ink))]">
                <span className="font-eyebrow text-[hsl(var(--md-stone))] mr-2">{labels[sub.labelKey]}</span>
                {item[sub.key]}
              </p>
            ) : null
          ))}
        </li>
      ))}
    </ul>
  );
}

// -----------------------------------------------------------------------------
// Colophon — mandatory on sealed Spaces per Codex §11
// -----------------------------------------------------------------------------

function Colophon({ space, t, lang }) {
  const rows = [
    [t.space.colophon.project, space.projectTitle],
    [t.space.colophon.piece, space.pieceName],
    [t.space.colophon.opened, editorialDate(space.studyOpenedOn, lang)],
    [t.space.colophon.sealed, editorialDate(space.sealed?.date, lang)],
  ];
  if (space.pieceNumber) {
    rows.push([t.space.colophon.pieceNumber, space.pieceNumber]);
  }

  return (
    <section className="mt-24 md:mt-28" data-testid="space-colophon">
      <div className="chapter-hairline mb-8" />
      <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-8">
        {t.space.colophon.title}
      </p>
      <dl className="space-y-3">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-12 gap-4">
            <dt className="col-span-12 md:col-span-4 font-eyebrow text-[hsl(var(--md-stone))]">
              {k}
            </dt>
            <dd className="col-span-12 md:col-span-8 font-body text-[15px] text-[hsl(var(--md-ink))]">
              {v || "—"}
            </dd>
          </div>
        ))}
      </dl>
      <div className="mt-10">
        <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-2">
          {t.space.colophon.signature}
        </p>
        <p
          className="font-display italic text-[1.5rem] text-[hsl(var(--md-ink))]"
          data-testid="space-signature"
        >
          {space.sealed?.by || "Maison Dodji"}
        </p>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Not-found state — sober, editorial, no error tone
// -----------------------------------------------------------------------------

function SpaceNotFound({ t }) {
  return (
    <div
      className="min-h-screen bg-[hsl(var(--md-chalk))] text-[hsl(var(--md-ink))] flex items-center"
      data-testid="space-not-found"
    >
      <div className="max-w-[720px] mx-auto px-6 md:px-12 py-24 text-center">
        <h1 className="font-display text-[2rem] md:text-[2.5rem] leading-[1.15] mb-8">
          {t.space.notFoundTitle}
        </h1>
        <p className="font-body text-[16px] leading-[1.8] text-[hsl(var(--md-stone))] mb-12">
          {t.space.notFoundBody}
        </p>
        <Link
          to="/"
          className="font-eyebrow text-[hsl(var(--md-ink))] border-b border-[hsl(var(--md-line-strong))]"
          data-testid="space-notfound-return"
        >
          {t.space.returnMaison}
        </Link>
      </div>
    </div>
  );
}
